// Vendor-only theme system — clone of portalTheme.tsx with its own
// storage namespace.
//
// Why a separate provider (mirrors the portal-vs-staff rationale):
//   - Storage key namespace: 'orkestra-vendor-theme' must not clobber
//     'orkestra-portal-theme' or the staff app's 'orkestra-theme'. A
//     subcontractor who also happens to be a client (or staff) in the
//     same browser keeps three independent appearance preferences.
//   - Scope: applies data-theme to a vendor wrapper element, not
//     document.documentElement — same reasoning as the portal.
//   - No profile sync: vendor users have profile === null. The staff
//     provider's profile.appearance branch is a no-op for them anyway.
//
// The brand engine itself (lib/portalBrand) is portal-agnostic and is
// reused here as-is — it does pure RGB/RGBA/contrast math against the
// brand color and the resolved theme; there's nothing portal-specific
// about it.

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { MotionConfig } from 'framer-motion'
import { deriveBrandOverrides } from '@/lib/portalBrand'

export type VendorTheme = 'light' | 'dark' | 'system'
export type ResolvedVendorTheme = 'light' | 'dark'

const STORAGE_KEY = 'orkestra-vendor-theme'

interface Ctx {
  theme: VendorTheme
  resolvedTheme: ResolvedVendorTheme
  setTheme: (next: VendorTheme) => void
  toggle: () => void
  brandColor: string | null
  setBrandColor: (next: string | null) => void
}

const VendorThemeContext = createContext<Ctx | null>(null)

function readStored(): VendorTheme {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw === 'light' || raw === 'dark' || raw === 'system') return raw
  } catch { /* SSR / private mode */ }
  return 'system'
}

function resolveTheme(t: VendorTheme): ResolvedVendorTheme {
  if (t !== 'system') return t
  const prefersDark = typeof window !== 'undefined'
    && window.matchMedia?.('(prefers-color-scheme: dark)').matches
  return prefersDark ? 'dark' : 'light'
}

export function VendorThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<VendorTheme>(() => readStored())
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedVendorTheme>(() => resolveTheme(readStored()))
  const [brandColor, setBrandColor] = useState<string | null>(null)

  useEffect(() => { setResolvedTheme(resolveTheme(theme)) }, [theme])

  useEffect(() => {
    if (theme !== 'system') return
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    const handler = () => setResolvedTheme(mq.matches ? 'dark' : 'light')
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [theme])

  const setTheme = useCallback((next: VendorTheme) => {
    setThemeState(next)
    try { localStorage.setItem(STORAGE_KEY, next) } catch { /* ignore */ }
  }, [])

  const toggle = useCallback(() => {
    setTheme(resolvedTheme === 'dark' ? 'light' : 'dark')
  }, [resolvedTheme, setTheme])

  const value = useMemo<Ctx>(
    () => ({ theme, resolvedTheme, setTheme, toggle, brandColor, setBrandColor }),
    [theme, resolvedTheme, setTheme, toggle, brandColor],
  )

  const brandStyle = useMemo(
    () => deriveBrandOverrides(brandColor, resolvedTheme),
    [brandColor, resolvedTheme],
  )

  return (
    <VendorThemeContext.Provider value={value}>
      <MotionConfig reducedMotion="user">
        <div data-theme={resolvedTheme} style={{ minHeight: '100vh', ...brandStyle }}>
          {children}
        </div>
      </MotionConfig>
    </VendorThemeContext.Provider>
  )
}

export function useVendorTheme(): Ctx {
  const ctx = useContext(VendorThemeContext)
  if (!ctx) throw new Error('useVendorTheme must be used inside <VendorThemeProvider>')
  return ctx
}
